/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { UserRole } from '../../../types/access'

export interface StudyEnvironment {
  fsiId: number
  studyName: string
  protocolId: string
  envName: string
  app: string // API returns app, not clinicalApp
}

export interface StudySite {
  fsiId: number
  siteName: string
  siteNum: string // API returns siteNum, not siteNumber
  studyName: string
  envName: string
  app: string // API returns app, not clinicalApp
}

export interface StudyTrial {
  fsiId: number
  studyName: string
  protocolId: string
  app: string // API returns app, not clinicalApp
}

export interface PaginatedResponse<T> {
  elements: T[]
  totalElements: number
}

export interface FilterOption {
  value: string
  label: string
}

export interface StudiesFilter {
  myEnvironments: FilterOption[] | null
  myStudies: FilterOption[] | null
}

export interface SitesFilter {
  mySites: FilterOption[] | null
}

export interface TableDataParams {
  sortBy: string
  sortDesc: boolean
  pageStart: number
  pageSize: number
  fsiIds?: number[]
}

export enum ClinicalApp {
  ETMF = 'ETMF',
  RACT = 'RACT',
  SIM = 'SIM'
}

export type StudyTableMode = 'environments' | 'studies'

export interface OverviewConfig {
  showStudies: boolean
  showSites: boolean
  studyTableMode: StudyTableMode
  isFullWidth: boolean
  userRole?: UserRole
}
export const roleConfigMap: Record<UserRole, OverviewConfig> = {
  [UserRole.ETMF_STUDY_USER]: {
    showStudies: true,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.ETMF_STUDY_USER
  },
  [UserRole.ETMF_SPECIALIST]: {
    showStudies: true,
    showSites: true,
    studyTableMode: 'environments',
    isFullWidth: false,
    userRole: UserRole.ETMF_SPECIALIST
  },
  [UserRole.RCR_USER]: {
    showStudies: true,
    showSites: true,
    studyTableMode: 'environments',
    isFullWidth: false,
    userRole: UserRole.RCR_USER
  },
  [UserRole.ETMF_SPONSOR]: {
    showStudies: true,
    showSites: true,
    studyTableMode: 'environments',
    isFullWidth: false,
    userRole: UserRole.ETMF_SPONSOR
  },
  [UserRole.ETMF_MANAGER]: {
    showStudies: true,
    showSites: true,
    studyTableMode: 'studies',
    isFullWidth: false,
    userRole: UserRole.ETMF_MANAGER
  },
  [UserRole.ETMF_SITE_USER]: {
    showStudies: false,
    showSites: true,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.ETMF_SITE_USER
  },
  // Default fallback for other roles
  [UserRole.ADMIN]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.ADMIN
  },
  [UserRole.CONTENT_ADMIN]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.CONTENT_ADMIN
  },
  [UserRole.POWER_USER]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.POWER_USER
  },
  [UserRole.USER]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.USER
  },
  [UserRole.AUDITOR]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.AUDITOR
  },
  [UserRole.INSPECTOR]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.INSPECTOR
  },
  [UserRole.EXTERNAL]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.EXTERNAL
  },
  [UserRole.CLOUD_ADMIN_ROLE]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.CLOUD_ADMIN_ROLE
  },
  [UserRole.EOS_SITE_USER]: {
    showStudies: false,
    showSites: false,
    studyTableMode: 'environments',
    isFullWidth: true,
    userRole: UserRole.EOS_SITE_USER
  }
}
